import { z } from "zod";
import {
  commandContextSchema,
  commandError,
  stableFingerprint,
  type AuditEntry,
  type CommandContext,
  type CommandReceipt,
  type CommandResult,
  type CommandSuccess,
} from "../../lib/command-contract";
import type { NoticeState } from "./feed";

/**
 * Comandos da central de notificações.
 *
 * O feed é derivado (ver `feed.ts`); o que se persiste aqui é só a decisão do
 * usuário sobre cada aviso, endereçada pela chave estável. Duas consequências
 * que os comandos preservam:
 *
 * - **Ler e resolver são marcas diferentes.** Marcar como lida não encerra a
 *   pendência; resolver encerra o aviso e mantém a causa intacta — a tarefa
 *   continua vencida até ser concluída pelo comando dela.
 * - **Resolver exige que o aviso exista e seja tratável.** Confirmar resolução
 *   de um aviso informativo, ou de uma chave que não está no feed corrente,
 *   falha em vez de gravar uma marca órfã.
 */

export interface NotificationCommandState {
  states: NoticeState[];
  commandReceipts: CommandReceipt[];
  auditLog: AuditEntry[];
  /** Chaves presentes no feed corrente; sem elas nenhuma marca é gravada. */
  availableKeys?: readonly string[];
  /** Subconjunto tratável: só ele aceita resolução. */
  actionableKeys?: readonly string[];
}

export interface NotificationCommandValue {
  states: NoticeState[];
  /** Quantas chaves a operação realmente alterou. */
  changed: number;
}

export interface NotificationCommandDeps {
  now: () => string;
  uuid: () => string;
}

export interface NotificationCommandExecution {
  result: CommandResult<NotificationCommandValue>;
  state: NotificationCommandState;
}

const INVALID_CONTEXT: CommandContext = Object.freeze({
  ownerId: "invalid-context",
  actorType: "system",
  actorId: "notification-command",
  origin: "system",
  commandId: "invalid-context",
  correlationId: "invalid-context",
  idempotencyKey: "invalid-context",
  requestedAt: "1970-01-01T00:00:00.000Z",
  schemaVersion: 1,
});

const noticeKeySchema = z.string().trim().min(1, "Informe o aviso.").max(200);

export const markNoticeReadSchema = z.object({
  noticeKey: noticeKeySchema,
  read: z.boolean().default(true),
});
export const resolveNoticeSchema = z.object({
  noticeKey: noticeKeySchema,
  resolved: z.boolean().default(true),
});
export const markAllNoticesReadSchema = z.object({
  noticeKeys: z.array(noticeKeySchema).min(1, "Nenhum aviso para marcar."),
});

export type MarkNoticeReadInput = z.input<typeof markNoticeReadSchema>;
export type ResolveNoticeInput = z.input<typeof resolveNoticeSchema>;
export type MarkAllNoticesReadInput = z.input<typeof markAllNoticesReadSchema>;

function cloneState(state: NotificationCommandState): NotificationCommandState {
  return {
    states: state.states.map((item) => ({ ...item })),
    commandReceipts: [...state.commandReceipts],
    auditLog: [...state.auditLog],
    availableKeys: state.availableKeys,
    actionableKeys: state.actionableKeys,
  };
}

function checkedContext(context: unknown): CommandContext | undefined {
  const parsed = commandContextSchema.safeParse(context);
  return parsed.success ? parsed.data : undefined;
}

function fail(
  state: NotificationCommandState,
  context: CommandContext,
  code: Parameters<typeof commandError>[1],
  message: string,
): NotificationCommandExecution {
  return {
    state,
    result: commandError(context, code, message, {
      severity: code === "persistence_error" ? "error" : "warning",
    }),
  };
}

function replayLookup(
  state: NotificationCommandState,
  context: CommandContext,
  command: string,
  fingerprint: string,
): NotificationCommandExecution | null {
  const receipt = state.commandReceipts.find(
    (item) =>
      item.ownerId === context.ownerId &&
      item.command === command &&
      item.idempotencyKey === context.idempotencyKey,
  );
  if (!receipt) return null;
  if (receipt.requestFingerprint !== fingerprint)
    return fail(
      state,
      context,
      "idempotency_conflict",
      "A chave de idempotência já foi usada para outra intenção.",
    );
  if (!receipt.result.ok)
    return { state, result: receipt.result as CommandResult<NotificationCommandValue> };
  return {
    state,
    result: {
      ...(receipt.result as CommandSuccess<NotificationCommandValue>),
      status: "replayed",
    },
  };
}

interface FinishInput {
  state: NotificationCommandState;
  context: CommandContext;
  command: string;
  fingerprint: string;
  targetId: string;
  value: NotificationCommandValue;
  nextState: NotificationCommandState;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  deps: NotificationCommandDeps;
}

function finish(input: FinishInput): NotificationCommandExecution {
  const occurredAt = input.deps.now();
  const auditId = input.deps.uuid();
  const result: CommandSuccess<NotificationCommandValue> = {
    ok: true,
    status: "applied",
    value: input.value,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    version: 1,
    eventIds: [],
    outboxJobIds: [],
    auditId,
    warnings: [],
  };
  const audit: AuditEntry = {
    id: auditId,
    ownerId: input.context.ownerId,
    command: input.command,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    causationId: input.context.causationId,
    actorType: input.context.actorType,
    actorId: input.context.actorId,
    origin: input.context.origin,
    targetType: "notification_state",
    targetId: input.targetId,
    idempotencyKey: input.context.idempotencyKey,
    requestFingerprint: input.fingerprint,
    result: "applied",
    occurredAt,
    before: input.before,
    after: input.after,
  };
  const receipt: CommandReceipt = {
    id: input.deps.uuid(),
    ownerId: input.context.ownerId,
    command: input.command,
    idempotencyKey: input.context.idempotencyKey,
    requestFingerprint: input.fingerprint,
    commandId: input.context.commandId,
    correlationId: input.context.correlationId,
    result,
    createdAt: occurredAt,
    schemaVersion: input.context.schemaVersion,
  };
  return {
    result,
    state: {
      ...input.nextState,
      auditLog: [...input.nextState.auditLog, audit],
      commandReceipts: [...input.nextState.commandReceipts, receipt],
    },
  };
}

function validationMessage(error: unknown): string {
  if (error instanceof z.ZodError) return error.issues[0]?.message ?? "Dados inválidos.";
  return error instanceof Error ? error.message : "Dados inválidos.";
}

function knows(state: NotificationCommandState, key: string): boolean {
  return state.availableKeys === undefined || state.availableKeys.includes(key);
}

/** Aplica uma mudança sobre a linha da chave, criando-a quando necessário. */
function upsert(
  states: readonly NoticeState[],
  noticeKey: string,
  change: (current: NoticeState) => NoticeState,
): { states: NoticeState[]; changed: boolean; before?: NoticeState; after: NoticeState } {
  const current = states.find((item) => item.noticeKey === noticeKey) ?? { noticeKey };
  const next = change({ ...current });
  const changed =
    current.readAt !== next.readAt || current.resolvedAt !== next.resolvedAt;
  const withoutKey = states.filter((item) => item.noticeKey !== noticeKey);
  // Linha sem nenhuma marca não é gravada: desmarcar tudo devolve o aviso ao
  // estado natural em vez de deixar um registro vazio para sempre.
  const list =
    next.readAt || next.resolvedAt
      ? [...withoutKey, next]
      : withoutKey;
  return {
    states: list,
    changed,
    before: states.find((item) => item.noticeKey === noticeKey),
    after: next,
  };
}

export function executeMarkNoticeRead(
  stateInput: NotificationCommandState,
  input: MarkNoticeReadInput,
  contextInput: CommandContext,
  deps: NotificationCommandDeps,
): NotificationCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof markNoticeReadSchema>;
  try {
    draft = markNoticeReadSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "notifications.mark_read", fingerprint);
  if (replay) return replay;

  if (!knows(state, draft.noticeKey))
    return fail(stateInput, context, "not_found", "Este aviso não está no feed atual.");

  const now = deps.now();
  const applied = upsert(state.states, draft.noticeKey, (current) => ({
    ...current,
    readAt: draft.read ? (current.readAt ?? now) : undefined,
  }));

  return finish({
    state,
    context,
    command: "notifications.mark_read",
    fingerprint,
    targetId: draft.noticeKey,
    value: { states: applied.states, changed: applied.changed ? 1 : 0 },
    nextState: { ...state, states: applied.states },
    before: applied.before ? { ...applied.before } : undefined,
    after: { ...applied.after },
    deps,
  });
}

export function executeResolveNotice(
  stateInput: NotificationCommandState,
  input: ResolveNoticeInput,
  contextInput: CommandContext,
  deps: NotificationCommandDeps,
): NotificationCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof resolveNoticeSchema>;
  try {
    draft = resolveNoticeSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "notifications.resolve", fingerprint);
  if (replay) return replay;

  if (!knows(state, draft.noticeKey))
    return fail(stateInput, context, "not_found", "Este aviso não está no feed atual.");
  if (
    draft.resolved &&
    state.actionableKeys !== undefined &&
    !state.actionableKeys.includes(draft.noticeKey)
  )
    return fail(
      stateInput,
      context,
      "invalid_state",
      "Este aviso é informativo e não pede confirmação de resolução.",
    );

  const now = deps.now();
  const applied = upsert(state.states, draft.noticeKey, (current) => ({
    ...current,
    // Resolver também marca como lida: o usuário acabou de olhar o aviso.
    readAt: draft.resolved ? (current.readAt ?? now) : current.readAt,
    resolvedAt: draft.resolved ? (current.resolvedAt ?? now) : undefined,
  }));

  return finish({
    state,
    context,
    command: "notifications.resolve",
    fingerprint,
    targetId: draft.noticeKey,
    value: { states: applied.states, changed: applied.changed ? 1 : 0 },
    nextState: { ...state, states: applied.states },
    before: applied.before ? { ...applied.before } : undefined,
    after: { ...applied.after },
    deps,
  });
}

/**
 * Marca como lidas as chaves informadas.
 *
 * A lista vem do feed que o usuário está vendo: "marcar todas" nunca alcança um
 * aviso escondido por filtro, categoria silenciada ou aba diferente.
 */
export function executeMarkAllNoticesRead(
  stateInput: NotificationCommandState,
  input: MarkAllNoticesReadInput,
  contextInput: CommandContext,
  deps: NotificationCommandDeps,
): NotificationCommandExecution {
  const context = checkedContext(contextInput);
  if (!context)
    return fail(stateInput, INVALID_CONTEXT, "validation_error", "Contexto de comando inválido.");
  let draft: z.output<typeof markAllNoticesReadSchema>;
  try {
    draft = markAllNoticesReadSchema.parse(input);
  } catch (error) {
    return fail(stateInput, context, "validation_error", validationMessage(error));
  }
  const fingerprint = stableFingerprint(draft);
  const state = cloneState(stateInput);
  const replay = replayLookup(state, context, "notifications.mark_all_read", fingerprint);
  if (replay) return replay;

  const unknown = draft.noticeKeys.filter((key) => !knows(state, key));
  if (unknown.length > 0)
    return fail(
      stateInput,
      context,
      "not_found",
      `${unknown.length} aviso(s) já não estão no feed atual.`,
    );

  const now = deps.now();
  let states = state.states;
  let changed = 0;
  for (const key of draft.noticeKeys) {
    const applied = upsert(states, key, (current) => ({
      ...current,
      readAt: current.readAt ?? now,
    }));
    states = applied.states;
    if (applied.changed) changed += 1;
  }

  return finish({
    state,
    context,
    command: "notifications.mark_all_read",
    fingerprint,
    targetId: `${draft.noticeKeys.length} avisos`,
    value: { states, changed },
    nextState: { ...state, states },
    after: { changed },
    deps,
  });
}

export interface NotificationDemoAdapter {
  markRead(
    input: MarkNoticeReadInput,
    context: CommandContext,
  ): Promise<CommandResult<NotificationCommandValue>>;
  resolve(
    input: ResolveNoticeInput,
    context: CommandContext,
  ): Promise<CommandResult<NotificationCommandValue>>;
  markAllRead(
    input: MarkAllNoticesReadInput,
    context: CommandContext,
  ): Promise<CommandResult<NotificationCommandValue>>;
  getState(): NotificationCommandState;
  syncExternal(state: NotificationCommandState): void;
}

export function createNotificationDemoAdapter(
  initialState: NotificationCommandState,
  deps: NotificationCommandDeps,
  onCommit?: (state: NotificationCommandState) => void,
): NotificationDemoAdapter {
  let state = cloneState(initialState);
  let queue: Promise<unknown> = Promise.resolve();
  let busy = false;

  const enqueue = (
    operation: () => NotificationCommandExecution,
  ): Promise<CommandResult<NotificationCommandValue>> => {
    const run = async () => {
      busy = true;
      try {
        const execution = operation();
        if (execution.result.ok && execution.result.status === "applied") {
          state = execution.state;
          onCommit?.(state);
        }
        return execution.result;
      } finally {
        busy = false;
      }
    };
    if (!busy) {
      const started = run();
      queue = started.then(
        () => undefined,
        () => undefined,
      );
      return started;
    }
    const next = queue.then(run);
    queue = next.then(
      () => undefined,
      () => undefined,
    );
    return next as Promise<CommandResult<NotificationCommandValue>>;
  };

  return {
    markRead: (input, context) => enqueue(() => executeMarkNoticeRead(state, input, context, deps)),
    resolve: (input, context) => enqueue(() => executeResolveNotice(state, input, context, deps)),
    markAllRead: (input, context) =>
      enqueue(() => executeMarkAllNoticesRead(state, input, context, deps)),
    getState: () => state,
    syncExternal: (next) => {
      if (!busy) state = cloneState(next);
    },
  };
}
